import AuthenticationActions, {
  AuthenticationSelectors
} from "Redux/AuthenticationRedux";
import { call, put, select } from "redux-saga/effects";
import { isTokenExpired } from "Utils/Interceptor";
import { AuthenticationApi } from "Services/ApiServices";
import ProfileActions from "Redux/ProfileRedux";

const camelizeKeys = require("camelcase-keys");
const decamelizeKeysDeep = require("decamelize-keys-deep");

const authApi = AuthenticationApi.create();

// --- Saga for refreshing the access token --- //
export function* refreshToken(originalAction) {
  const refreshToken = yield select(AuthenticationSelectors.getRefreshToken);
  const response = yield call(authApi.refreshToken, refreshToken);
  if (response.status === 200) {
    yield put(AuthenticationActions.refreshTokenSuccess(response.data));
    yield put(originalAction);
  } else {
    yield put(AuthenticationActions.refreshTokenFailure(response.data));
  }
}

// --- This checks if the user's tokens are correct (i.e. useful for refreshing the page) --- //
export function* authenticationCheckSaga(api) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.authenticationCheckApi, token);
  if (response.status === 200) {
    yield put(AuthenticationActions.authenticationCheckSuccess(response.data));
    yield put(ProfileActions.profileAuthenticationCheckSuccess(response.data));
  } else {
    yield put(AuthenticationActions.authenticationCheckFailure(response.data));
  }
}

// --- This sends the password reset link --- //
export function* resetLinkSaga(api, { data }) {
  const { values, submissionError, submissionSuccess } = data;
  const response = yield call(api.resetLinkApi, values);
  if (response.status === 200) {
    submissionSuccess();
    yield put(AuthenticationActions.resetLinkSuccess(response.data));
  } else {
    submissionError(response.data);
    yield put(AuthenticationActions.resetLinkFailure(response.data));
  }
}

// --- This resets the password --- //
export function* passwordResetSaga(api, { data }) {
  const { values, submissionError, submissionSuccess } = data;
  const token = yield select(AuthenticationSelectors.getAccessToken);
  let response;
  if (values.oldPassword) {
    response = yield call(api.passwordSettingsResetApi, {
      token: token,
      data: values
    });
  } else {
    response = yield call(api.passwordResetApi, { token, data });
  }
  if (response.status === 200) {
    yield put(AuthenticationActions.passwordResetSuccess(response.data));
    submissionSuccess();
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        AuthenticationActions.passwordResetRequest(data)
      );
    } else {
      yield put(AuthenticationActions.passwordResetFailure(response.data));
      submissionError(response.data);
    }
  }
}

// --- This logs the user in --- //
export function* login(api, { data, resolve }) {
  const credentials = { ...data };
  const response = yield call(api.login, credentials);
  if (response.status === 200) {
    yield put(AuthenticationActions.loginSuccess(response.data));
    yield call(resolve.onSuccess);
  } else {
    yield put(AuthenticationActions.loginFailure(response.data));
    yield call(resolve.onFailure, response.data);
  }
}

// --- This registers the user --- //
export function* register(api, { data, resolve }) {
  const response = yield call(api.register, data);
  if (response.status === 201) {
    yield put(AuthenticationActions.registerSuccess(response.data));
    yield call(resolve.onSuccess);
  } else {
    yield put(AuthenticationActions.registerFailure(response.data));
    yield call(resolve.onFailure, response.data);
  }
}

// --- This authenticates the user's email --- //
export function* authenticateEmailSaga(api, action) {
  const response = yield call(api.authenticateEmailApi, action.data);
  if (response.status === 200) {
    yield put(AuthenticationActions.authenticateEmailSuccess(response.data));
  } else {
    yield put(AuthenticationActions.authenticateEmailFailure(response.data));
  }
}

// --- Social Auth Login --- //
export function* socialAuthLoginSaga(api, { data, resolve }) {
  const response = yield call(api.socialLogin, decamelizeKeysDeep(data, "_"));
  if (response.status === 200) {
    yield put(AuthenticationActions.socialAuthLoginSuccess(response.data));
    yield call(resolve.onSuccess);
  } else {
    yield put(AuthenticationActions.socialAuthLoginFailure(response.data));
    yield call(resolve.onFailure, response.data);
  }
}

// --- Get User Details --- //
export function* getUserDetailsSaga(api) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.getUserDetails, token);
  const camelizedData = camelizeKeys(response.data);
  if (response.status === 200) {
    yield put(AuthenticationActions.getUserDetailsSuccess(camelizedData));
  } else {
    if (isTokenExpired(response.data)) {
      yield call(refreshToken, AuthenticationActions.getUserDetailsRequest());
    } else {
      yield put(AuthenticationActions.getUserDetailsFailure, camelizedData);
    }
  }
}

// --- Update User Details --- //
export function* updateUserDetailsSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.updateUserDetails, {
    token,
    data: decamelizeKeysDeep(data)
  });
  const camelizedData = camelizeKeys(response.data);
  if (response.status === 200) {
    yield put(AuthenticationActions.updateUserDetailsSuccess(camelizedData));
    resolve.onSuccess();
  } else {
    yield put(AuthenticationActions.updateUserDetailsFailure(camelizedData));
    yield call(resolve.onFailure, response.data);
  }
}
